import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAllCourses,
  deleteCourse,
  activateCourse,
  deactivateCourse,
} from "./adminCourseService";
import { isOfferActive } from "../../user_module/components/courses/CourseOfferBadge";
import "./adminCourses.css";

const LEVEL_BADGE = {
  BEGINNER: "ac-badge-green",
  INTERMEDIATE: "ac-badge-yellow",
  PROFESSIONAL: "ac-badge-purple",
  SPEAK_COMPLETE: "ac-badge-rose",
};

const LEVEL_LABEL = {
  BEGINNER: "Beginner",
  INTERMEDIATE: "Intermediate",
  PROFESSIONAL: "Professional",
  SPEAK_COMPLETE: "Speak Complete",
};

const fmt = (paise) =>
  paise != null ? `₹${(paise / 100).toLocaleString("en-IN")}` : "—";

const AdminCoursesPage = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchAllCourses();
      setCourses(res.data || []);
    } catch {
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (course) => {
    setTogglingId(course.id);
    try {
      if (course.isActive) {
        await deactivateCourse(course.id);
      } else {
        await activateCourse(course.id);
      }
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Action failed.";
      alert(msg);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Delete "${course.name}"? This cannot be undone.`)) return;
    try {
      await deleteCourse(course.id);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Delete failed.";
      alert(msg);
    }
  };

  return (
    <div>
      <div className="ac-header">
        <h2>Courses</h2>
        <div className="ac-actions">
          <button
            className="ac-btn ac-btn-ghost"
            onClick={() => navigate("/admin/courses/payments")}
          >
            💳 Payments
          </button>
          <button
            className="ac-btn ac-btn-primary"
            onClick={() => navigate("/admin/courses/new")}
          >
            + Add Course
          </button>
        </div>
      </div>

      {loading && <div className="ac-loading">Loading courses…</div>}

      {!loading && courses.length === 0 && (
        <div className="ac-empty">No courses yet. Click "Add Course" to create one.</div>
      )}

      {!loading && courses.length > 0 && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Level</th>
                <th>Batch Price</th>
                <th>Personal Price</th>
                <th>Lessons</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                      {c.slug}
                    </div>
                    {isOfferActive(c.offer) && (
                      <span style={{ fontSize: "0.7rem", background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>
                        🏷️ {c.offer.offerDescription} · {c.offer.offerPercent}% OFF
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`ac-badge ${LEVEL_BADGE[c.level] || "ac-badge-gray"}`}>
                      {LEVEL_LABEL[c.level] || c.level}
                    </span>
                  </td>
                  <td>{c.batchModeEnabled ? fmt(c.priceBatch) : <span className="ac-badge ac-badge-gray">Off</span>}</td>
                  <td>{c.personalModeEnabled ? fmt(c.pricePersonal) : <span className="ac-badge ac-badge-gray">Off</span>}</td>
                  <td>{c.curriculumCount ?? "—"}</td>
                  <td>
                    <label className="ac-switch">
                      <input
                        type="checkbox"
                        checked={!!c.isActive}
                        disabled={togglingId === c.id}
                        onChange={() => handleToggleActive(c)}
                      />
                      <span className="ac-switch-slider" />
                    </label>
                  </td>
                  <td>
                    <div className="ac-actions">
                      <button
                        className="ac-btn ac-btn-ghost ac-btn-sm"
                        onClick={() => navigate(`/admin/courses/${c.id}/edit`)}
                      >
                        Edit
                      </button>
                      <button
                        className="ac-btn ac-btn-primary ac-btn-sm"
                        onClick={() => navigate(`/admin/courses/${c.id}/curriculum`)}
                      >
                        Curriculum
                      </button>
                      <button
                        className="ac-btn ac-btn-warning ac-btn-sm"
                        onClick={() => navigate(`/admin/courses/${c.id}/batches`)}
                      >
                        Batches
                      </button>
                      <button
                        className="ac-btn ac-btn-sm"
                        style={{ background: "#ede9fe", color: "#6d28d9" }}
                        onClick={() => navigate(`/admin/courses/${c.id}/demo`)}
                      >
                        Demo
                      </button>
                      <button
                        className="ac-btn ac-btn-sm"
                        style={{ background: isOfferActive(c.offer) ? "#fef3c7" : "#f3f4f6", color: isOfferActive(c.offer) ? "#92400e" : "#374151" }}
                        onClick={() => navigate(`/admin/courses/${c.id}/offer`)}
                      >
                        {isOfferActive(c.offer) ? "🏷️ Offer" : "+ Offer"}
                      </button>
                      <button
                        className="ac-btn ac-btn-danger ac-btn-sm"
                        onClick={() => handleDelete(c)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCoursesPage;
